import React, { useEffect, useState, useMemo } from 'react';
import { Table, Card, Button, Space, Typography, Input, Popconfirm, message, Modal, Form, Tabs, Row, Col } from 'antd';
import { PlusOutlined, SearchOutlined, DeleteOutlined, EditOutlined, ReloadOutlined, GlobalOutlined, EnvironmentOutlined, ApartmentOutlined } from '@ant-design/icons';
import StatCard from '../components/StatCard';
import useRegionStore from '../store/regionStore';

const { Title } = Typography;

const RegionsPage = () => {
  const {
    regions, districts, loading,
    fetchRegions, fetchDistricts,
    createRegion, updateRegion, deleteRegion,
    createDistrict, updateDistrict, deleteDistrict,
  } = useRegionStore();

  const [search, setSearch] = useState('');
  const [regionModal, setRegionModal] = useState(false);
  const [districtModal, setDistrictModal] = useState(false);
  const [editingRegion, setEditingRegion] = useState(null);
  const [editingDistrict, setEditingDistrict] = useState(null);
  const [regionForm] = Form.useForm();
  const [districtForm] = Form.useForm();

  useEffect(() => {
    fetchRegions();
    fetchDistricts();
  }, [fetchRegions, fetchDistricts]);

  const handleRegionSubmit = async (values) => {
    try {
      if (editingRegion) {
        await updateRegion(editingRegion.id, values);
        message.success('Yangilandi');
      } else {
        await createRegion(values);
        message.success("Qo'shildi");
      }
      setRegionModal(false);
      setEditingRegion(null);
      regionForm.resetFields();
      fetchRegions();
    } catch {
      message.error('Xatolik yuz berdi');
    }
  };

  const handleDistrictSubmit = async (values) => {
    try {
      if (editingDistrict) {
        await updateDistrict(editingDistrict.id, values);
        message.success('Yangilandi');
      } else {
        await createDistrict(values);
        message.success("Qo'shildi");
      }
      setDistrictModal(false);
      setEditingDistrict(null);
      districtForm.resetFields();
      fetchDistricts();
    } catch {
      message.error('Xatolik yuz berdi');
    }
  };

  const regionColumns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 60 },
    { title: 'Nomi (UZ)', dataIndex: 'nameuz', key: 'nameuz' },
    { title: 'Nomi (RU)', dataIndex: 'nameru', key: 'nameru' },
    { title: 'Nomi (EN)', dataIndex: 'nameen', key: 'nameen' },
    {
      title: 'Amallar',
      key: 'actions',
      width: 120,
      render: (_, record) => (
        <Space>
          <Button type="text" icon={<EditOutlined />} onClick={() => {
            setEditingRegion(record);
            regionForm.setFieldsValue(record);
            setRegionModal(true);
          }} />
          <Popconfirm title="O'chirishni tasdiqlaysizmi?" onConfirm={async () => {
            try { await deleteRegion(record.id); message.success("O'chirildi"); fetchRegions(); }
            catch { message.error('Xatolik'); }
          }}>
            <Button type="text" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const districtColumns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 60 },
    { title: 'Nomi (UZ)', dataIndex: 'nameuz', key: 'nameuz' },
    { title: 'Nomi (RU)', dataIndex: 'nameru', key: 'nameru' },
    { title: 'Nomi (EN)', dataIndex: 'nameen', key: 'nameen' },
    {
      title: 'Viloyat ID',
      dataIndex: 'region_id',
      key: 'region_id',
      render: (v) => {
        const region = regions?.find((r) => r.id === v);
        return region ? region.nameuz : v;
      },
    },
    {
      title: 'Amallar',
      key: 'actions',
      width: 120,
      render: (_, record) => (
        <Space>
          <Button type="text" icon={<EditOutlined />} onClick={() => {
            setEditingDistrict(record);
            districtForm.setFieldsValue(record);
            setDistrictModal(true);
          }} />
          <Popconfirm title="O'chirishni tasdiqlaysizmi?" onConfirm={async () => {
            try { await deleteDistrict(record.id); message.success("O'chirildi"); fetchDistricts(); }
            catch { message.error('Xatolik'); }
          }}>
            <Button type="text" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const filteredRegions = regions?.filter((r) =>
    !search || (r.nameuz || '').toLowerCase().includes(search.toLowerCase()) || (r.nameru || '').toLowerCase().includes(search.toLowerCase())
  );

  const filteredDistricts = districts?.filter((d) =>
    !search || (d.nameuz || '').toLowerCase().includes(search.toLowerCase()) || (d.nameru || '').toLowerCase().includes(search.toLowerCase())
  );

  const tabItems = [
    {
      key: 'regions',
      label: 'Viloyatlar',
      children: (
        <div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => { setEditingRegion(null); regionForm.resetFields(); setRegionModal(true); }}>
              Viloyat qo'shish
            </Button>
          </div>
          <Table dataSource={filteredRegions} columns={regionColumns} rowKey="id" loading={loading} scroll={{ x: 600 }} pagination={{ pageSize: 15, showTotal: (t) => `Jami: ${t}` }} />
        </div>
      ),
    },
    {
      key: 'districts',
      label: 'Tumanlar',
      children: (
        <div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => { setEditingDistrict(null); districtForm.resetFields(); setDistrictModal(true); }}>
              Tuman qo'shish
            </Button>
          </div>
          <Table dataSource={filteredDistricts} columns={districtColumns} rowKey="id" loading={loading} scroll={{ x: 700 }} pagination={{ pageSize: 15, showTotal: (t) => `Jami: ${t}` }} />
        </div>
      ),
    },
  ];

  const stats = useMemo(() => {
    const rList = regions || [];
    const dList = districts || [];
    return {
      totalRegions: rList.length,
      totalDistricts: dList.length,
      avgDistricts: rList.length > 0 ? Math.round(dList.length / rList.length) : 0,
    };
  }, [regions, districts]);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
        <Title level={3} style={{ margin: 0 }}>Hududlar</Title>
        <Space wrap>
          <Input placeholder="Qidirish..." prefix={<SearchOutlined />} value={search} onChange={(e) => setSearch(e.target.value)} style={{ width: 200 }} allowClear />
          <Button icon={<ReloadOutlined />} onClick={() => { fetchRegions(); fetchDistricts(); }}>Yangilash</Button>
        </Space>
      </div>

      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={8}>
          <StatCard title="Jami viloyatlar" value={stats.totalRegions} icon={<GlobalOutlined />} color="#4f46e5" loading={loading} />
        </Col>
        <Col xs={24} sm={8}>
          <StatCard title="Jami tumanlar" value={stats.totalDistricts} icon={<EnvironmentOutlined />} color="#13c2c2" loading={loading} />
        </Col>
        <Col xs={24} sm={8}>
          <StatCard title="O'rtacha tuman/viloyat" value={stats.avgDistricts} icon={<ApartmentOutlined />} color="#722ed1" loading={loading} />
        </Col>
      </Row>

      <Card style={{ borderRadius: 12 }}>
        <Tabs items={tabItems} />
      </Card>

      <Modal
        title={editingRegion ? 'Viloyatni tahrirlash' : "Yangi viloyat qo'shish"}
        open={regionModal}
        onCancel={() => { setRegionModal(false); setEditingRegion(null); regionForm.resetFields(); }}
        onOk={() => regionForm.submit()}
        okText="Saqlash"
        cancelText="Bekor qilish"
      >
        <Form form={regionForm} layout="vertical" onFinish={handleRegionSubmit}>
          <Form.Item name="nameuz" label="Nomi (UZ)" rules={[{ required: true, message: 'Kiriting' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="nameru" label="Nomi (RU)" rules={[{ required: true, message: 'Kiriting' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="nameen" label="Nomi (EN)" rules={[{ required: true, message: 'Kiriting' }]}>
            <Input />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={editingDistrict ? 'Tumanni tahrirlash' : "Yangi tuman qo'shish"}
        open={districtModal}
        onCancel={() => { setDistrictModal(false); setEditingDistrict(null); districtForm.resetFields(); }}
        onOk={() => districtForm.submit()}
        okText="Saqlash"
        cancelText="Bekor qilish"
      >
        <Form form={districtForm} layout="vertical" onFinish={handleDistrictSubmit}>
          <Form.Item name="nameuz" label="Nomi (UZ)" rules={[{ required: true, message: 'Kiriting' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="nameru" label="Nomi (RU)" rules={[{ required: true, message: 'Kiriting' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="nameen" label="Nomi (EN)" rules={[{ required: true, message: 'Kiriting' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="region_id" label="Viloyat ID" rules={[{ required: true, message: 'Kiriting' }]}>
            <Input type="number" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default RegionsPage;
